/**
 * GeoHeatmapPanel
 *
 * Renders a `visualizations[0].type: 'geoheat'` report as a Google Maps
 * heatmap. The report's query must expose a single `country_code`
 * dimension — each row is mapped to the country's centroid and weighted
 * by the first metric.
 *
 * Lifecycle:
 *   1. Fetches aggregated data via `POST /il/analytics/:tenant/:site/report-query`
 *      (same endpoint every other ReportPanel uses).
 *   2. Lazy-loads the Google Maps JS API using `googleMapsBrowserJSApiKey`
 *      from `window.IL_EMBED_CONFIG` (Infralytiqs SDK bootstrap) or from
 *      `/system/client/config` (Redux, first-party app pages).
 *   3. Converts rows → `{lat, lng, weight}[]` using COUNTRY_CENTROIDS and
 *      feeds them into a custom canvas-based heatmap overlay (see
 *      `heatmapCanvasOverlay.ts`). The previous implementation used
 *      `google.maps.visualization.HeatmapLayer`, which Google deprecated
 *      in May 2025 and is removing in May 2026.
 *
 * The panel owns its own data fetch — callers (ReportPanel) must skip
 * their chart-path fetch for `type: 'geoheat'` to avoid duplicate
 * ClickHouse round-trips, the same pattern used for <PaginatedReportTable />.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress,
  Button, Stack, Tooltip, Typography, useTheme,
} from '@mui/material';
import { Refresh, FilterAlt, Public, EditOutlined } from '@mui/icons-material';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import { FilterDef, ReportDefinition, TimeRangeDef } from '../Settings/reportDsl';
import { loadGoogleMaps } from './googleMapsLoader';
import { useGoogleMapsApiKey } from './useGoogleMapsApiKey';
import { COUNTRY_CENTROIDS, getCentroidByCode } from './countryCentroids';
import { createHeatmapOverlay, HeatmapOverlay } from './heatmapCanvasOverlay';
import { reportsPanelToolbarButtonSx, reportsToolbarSurfaceBg } from './reportsToolbarSurface';
import { ReportPanelApiAction } from './ReportPanelApiAction';

interface GeoHeatmapPanelProps {
  report: ReportDefinition;
  tenantId: string;
  siteId?: string;
  timeRangeOverride?: TimeRangeDef;
  runtimeFilters?: FilterDef[];
  contextBadge?: string;
  /** IANA timezone for ClickHouse bucketing (see `ReportPanelProps.timezone`). */
  timezone?: string;
  /** When provided, shows an edit icon in the header to configure the panel. */
  onEdit?: (report: ReportDefinition) => void;
  /** When false, hide the panel ingest-API (API Specs) button. Defaults to true. */
  showApiAction?: boolean;
}

interface HeatPoint {
  code: string;
  name: string;
  lat: number;
  lng: number;
  weight: number;
}

/**
 * The dimension index on each row that holds the country code. We pick
 * whichever dimension has `field === 'country_code'`; fall back to the
 * first dimension so a slightly-differently-named custom field (e.g.
 * `country`) still works.
 */
const pickCountryDimIndex = (report: ReportDefinition): number => {
  const dims = report.query.dimensions || [];
  if (dims.length === 0) return -1;
  const byName = dims.findIndex((d) => d.field === 'country_code');
  return byName >= 0 ? byName : 0;
};

export const GeoHeatmapPanel: React.FC<GeoHeatmapPanelProps> = ({
  report, tenantId, siteId, timeRangeOverride, runtimeFilters, contextBadge, timezone, onEdit, showApiAction = true,
}) => {
  const theme = useTheme();
  const toolbarIconButtonSx = reportsPanelToolbarButtonSx(theme, {
    minWidth: 36, width: 36, height: 36, p: 0, borderRadius: 1,
  });
  const panelToolbarButtonColor = theme.palette.mode === 'light' ? undefined : 'secondary' as const;
  const vis = report.visualizations?.[0] || { type: 'geoheat' };
  const pathSite = useMemo(() => infralytiqsAnalyticsSitePathSegment(siteId), [siteId]);
  const mapsApiKey = useGoogleMapsApiKey();

  const containerRef = useRef<HTMLDivElement | null>(null);
  // Typed as `any` because we deliberately don't depend on @types/google.maps.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);
  const heatLayerRef = useRef<HeatmapOverlay | null>(null);

  const [rows, setRows] = useState<Record<string, any>[]>([]);
  /**
   * Precise points fetched from `POST /il/analytics/:t/:s/geo-points`. When
   * non-empty, these replace the country-centroid fallback — visitors who
   * opted in to browser geolocation are plotted at their actual coordinates
   * (bucketed server-side into a ~0.25° grid to group city-level density).
   */
  const [precisePoints, setPrecisePoints] = useState<
    Array<{ lat: number; lng: number; weight: number }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const dimIdx = useMemo(() => pickCountryDimIndex(report), [report]);

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    if (dimIdx < 0) {
      setFetchError('Geo heatmap requires a country dimension in the report query.');
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      // Run the country aggregate + precise point fetch in parallel. The
      // country result is the always-available fallback and also powers the
      // "Countries:" summary chip and the `unmapped` list. The precise
      // result, when populated, replaces the country centroids for rendering.
      const reportBody: Record<string, any> = {
        query: report.query,
        runtimeFilters: runtimeFilters || [],
        timeRangeOverride,
      };
      if (timezone) reportBody.timezone = timezone;
      // Prefer the toolbar override, fall back to the report's own timeRange
      // so the precise endpoint operates over exactly the same window as the
      // country aggregation displayed next to it.
      const geoPointsBody: Record<string, any> = {
        timeRange: timeRangeOverride ?? report.query.timeRange,
        bucket: 0.25,
        limit: 5000,
      };
      const userIdFilter = (runtimeFilters || []).find((f) => f.field === 'user_id' && f.value);
      if (userIdFilter && typeof userIdFilter.value === 'string') {
        geoPointsBody.userId = userIdFilter.value;
      }
      // The precise-points endpoint runs its own ClickHouse query (it does NOT
      // go through the report query builder), so the report's `page_url`
      // domain filter must be forwarded explicitly — otherwise opt-in points
      // from OTHER domains leak onto a domain-scoped suite's map. Runtime
      // filters take precedence over the report's own filter.
      const pageUrlFilter = [...(runtimeFilters || []), ...(report.query.filters || [])].find(
        (f) => f.field === 'page_url'
          && (f.op === 'contains' || f.op === 'eq' || f.op === 'starts_with')
          && typeof f.value === 'string'
          && f.value.trim() !== '',
      );
      if (pageUrlFilter && typeof pageUrlFilter.value === 'string') {
        geoPointsBody.pageUrl = pageUrlFilter.value.trim();
      }

      const [reportRes, geoRes] = await Promise.all([
        apiClient.post(
          `/il/analytics/${tenantId}/${pathSite}/report-query`,
          reportBody,
        ) as Promise<any>,
        // `/geo-points` only returns points for opted-in visitors. If
        // `captureLocation` is off for this domain this resolves to an
        // empty array and we transparently fall back to country centroids.
        apiClient
          .post(
            `/il/analytics/${tenantId}/${pathSite}/geo-points`,
            geoPointsBody,
          )
          .catch((): any => ({ points: [] })),
      ]);

      setRows(reportRes?.data ?? []);
      const rawPoints = Array.isArray((geoRes as any)?.points) ? (geoRes as any).points : [];
      setPrecisePoints(
        rawPoints
          .map((p: any) => ({
            lat: Number(p.lat),
            lng: Number(p.lng),
            weight: Number(p.weight),
          }))
          .filter(
            (p: { lat: number; lng: number; weight: number }) =>
              Number.isFinite(p.lat) && Number.isFinite(p.lng) && p.weight > 0,
          ),
      );
    } catch (err: any) {
      setFetchError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tenantId, pathSite, report.query, timeRangeOverride, runtimeFilters, dimIdx, timezone]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const countryPoints: HeatPoint[] = useMemo(() => {
    if (dimIdx < 0) return [];
    const out: HeatPoint[] = [];
    for (const row of rows) {
      const code = String(row[`dim_${dimIdx}`] || '').toUpperCase().trim();
      if (!code) continue;
      const c = getCentroidByCode(code);
      if (!c) continue; // unknown code → silently skip rather than misplacing it.
      const raw = row['metric_0'];
      const weight = typeof raw === 'number' ? raw : Number(raw) || 0;
      if (weight <= 0) continue;
      out.push({ code, name: c.name, lat: c.lat, lng: c.lng, weight });
    }
    return out;
  }, [rows, dimIdx]);

  /**
   * Render-ready points. When any precise (opt-in) coords exist we plot
   * those; otherwise we fall back to country centroids so the map still
   * conveys traffic distribution even without browser-level consent.
   */
  const points: HeatPoint[] = useMemo(() => {
    if (precisePoints.length === 0) return countryPoints;
    return precisePoints.map((p, idx) => ({
      code: `pt-${idx}`,
      name: `${p.lat.toFixed(2)}, ${p.lng.toFixed(2)}`,
      lat: p.lat,
      lng: p.lng,
      weight: p.weight,
    }));
  }, [precisePoints, countryPoints]);

  const usingPrecise = precisePoints.length > 0;

  /**
   * Initialise (or re-use) the Google Maps instance once the key is
   * available and the container element has mounted. Re-runs whenever
   * the panel is re-mounted — we purposely only create the Map once
   * per mount to avoid the constant-reinit flash when data refetches.
   */
  useEffect(() => {
    let cancelled = false;
    if (!mapsApiKey) {
      setMapError('Google Maps API key is not configured in the Infralytiqs embed SDK.');
      return;
    }
    if (!containerRef.current) return;

    loadGoogleMaps(mapsApiKey)
      .then((maps: any) => {
        if (cancelled || !containerRef.current) return;
        if (!mapRef.current) {
          mapRef.current = new maps.Map(containerRef.current, {
            center: { lat: 20, lng: 0 },
            zoom: 2,
            minZoom: 1,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            /*
             * `cooperative` is the right behavior for an embedded map on
             * a long, scrollable dashboard:
             *   - Mobile: a single-finger drag scrolls the page; two
             *     fingers are required to pan/zoom inside the map.
             *   - Desktop: Ctrl/⌘ + scroll wheel zooms; a plain wheel
             *     scrolls the page.
             * Without this the map captures every touch on phones and
             * the user gets stuck on the panel.
             */
            gestureHandling: 'cooperative',
            styles: [
              { featureType: 'poi', stylers: [{ visibility: 'off' }] },
              { featureType: 'transit', stylers: [{ visibility: 'off' }] },
              { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
            ],
          });
        }
        setMapError(null);
      })
      .catch((err: Error) => {
        if (!cancelled) setMapError(err.message || 'Failed to load Google Maps.');
      });

    return () => {
      cancelled = true;
    };
    // We intentionally depend only on the key — we do NOT recreate the
    // Map on data changes. Subsequent heat-data updates are pushed into
    // the existing `heatLayerRef` below.
  }, [mapsApiKey]);

  /**
   * Push the latest `points` into our custom canvas heatmap overlay,
   * (re)creating it on first run and updating data on subsequent runs
   * so the same canvas/DOM is reused (smoother updates, no flicker).
   *
   * The overlay replaces the deprecated
   * `google.maps.visualization.HeatmapLayer` (deprecated May 2025,
   * removed May 2026) — see `heatmapCanvasOverlay.ts`.
   */
  useEffect(() => {
    const map = mapRef.current;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const maps = (window as any).google?.maps;
    if (!map || !maps?.OverlayView) return;

    if (points.length === 0) {
      if (heatLayerRef.current) {
        heatLayerRef.current.setData([]);
      }
      return;
    }

    const weights = points.map((p) => p.weight);
    const total = weights.reduce((s, w) => s + w, 0);
    const maxW = weights.reduce((m, w) => Math.max(m, w), 0);
    // Same heuristic as the previous HeatmapLayer maxIntensity: clamp
    // the brightest blob so a single hot region doesn't wash out the
    // palette across the rest of the map.
    const maxIntensity = Math.max(5, Math.min(total, Math.round(maxW * 0.6)));

    const data = points.map((p) => ({ lat: p.lat, lng: p.lng, weight: p.weight }));

    if (!heatLayerRef.current) {
      heatLayerRef.current = createHeatmapOverlay(maps, {
        radius: 40,
        opacity: 0.75,
        maxIntensity,
      });
      heatLayerRef.current.setMap(map);
    }
    heatLayerRef.current.setData(data, { maxIntensity });
  }, [points]);

  useEffect(() => {
    // Dispose Maps resources on unmount. We deliberately do NOT try to
    // mutate the map container DOM here — Google Maps already inserted
    // nodes into it and any manual cleanup would race React's own
    // unmount pass. Dropping the Map reference lets the V8/Blink GC
    // collect tiles/listeners once the container div is detached.
    return () => {
      try {
        if (heatLayerRef.current) {
          // setMap(null) detaches the canvas via OverlayView.onRemove().
          heatLayerRef.current.setMap(null);
          heatLayerRef.current = null;
        }
      } catch { /* ignore — map may already be gone */ }
      mapRef.current = null;
    };
  }, []);

  const topCountries = useMemo(
    () => [...countryPoints].sort((a, b) => b.weight - a.weight).slice(0, 3),
    [countryPoints],
  );
  const totalWeight = useMemo(
    () => points.reduce((s, p) => s + p.weight, 0),
    [points],
  );

  // Heuristic: warn about unmapped country codes so we can fill the
  // centroid table if a new locale starts appearing in traffic.
  const unmapped = useMemo(() => {
    if (dimIdx < 0) return [] as string[];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const row of rows) {
      const code = String(row[`dim_${dimIdx}`] || '').toUpperCase().trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      if (!COUNTRY_CENTROIDS[code]) out.push(code);
    }
    return out;
  }, [rows, dimIdx]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        className="il-panel-titlebar"
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: (t) => reportsToolbarSurfaceBg(t),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
          <Public fontSize="small" color="secondary" />
          <Typography variant="subtitle1" fontWeight={600} component="div">
            {vis.title || report.name}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={usingPrecise ? 'precise (opt-in)' : 'country-level'}
            color={usingPrecise ? 'secondary' : 'default'}
          />
          {contextBadge && (
            <Chip size="small" icon={<FilterAlt fontSize="small" />} label={contextBadge} color="secondary" />
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <ReportPanelApiAction
            report={report}
            tenantId={tenantId}
            siteId={siteId}
            runtimeFilters={runtimeFilters}
            visible={showApiAction}
          />
          {onEdit && (
            <Tooltip title="Edit panel configuration">
              <Button
                variant="outlined"
                color={panelToolbarButtonColor}
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit(report); }}
                aria-label="Edit report panel"
                sx={toolbarIconButtonSx}
              >
                <EditOutlined fontSize="small" />
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <Button
              variant="outlined"
              color={panelToolbarButtonColor}
              size="small"
              onClick={fetchData}
              aria-label="Refresh report"
              sx={toolbarIconButtonSx}
            >
              <Refresh fontSize="small" />
            </Button>
          </Tooltip>
        </Stack>
      </Box>
      <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {report.description || (report.query.timeRange?.preset ?? 'Custom range')}
        </Typography>
      </Box>
      <CardContent sx={{ flex: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {fetchError && <Alert severity="error">{fetchError}</Alert>}
        {!fetchError && mapError && <Alert severity="warning">{mapError}</Alert>}

        {/*
         * Summary strip above the map.
         *
         * Different headline metric depending on the data source:
         *   • Precise mode — we show "Points" (each point = a grid bucket,
         *     ~0.25°), since rolling up by country would lose the detail
         *     the user opted in to share.
         *   • Country mode — we show "Countries" + top-3 country weights
         *     + a warning chip for any ISO codes we couldn't map.
         */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            label={`Total: ${totalWeight.toLocaleString()}`}
            color="secondary"
            variant="outlined"
          />
          {usingPrecise ? (
            <Chip size="small" label={`Points: ${points.length}`} variant="outlined" />
          ) : (
            <>
              <Chip size="small" label={`Countries: ${countryPoints.length}`} variant="outlined" />
              {topCountries.map((p) => (
                <Chip
                  key={p.code}
                  size="small"
                  label={`${p.name}: ${p.weight.toLocaleString()}`}
                  variant="outlined"
                />
              ))}
              {unmapped.length > 0 && (
                <Tooltip title={`Country codes without a centroid: ${unmapped.join(', ')}`}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Unmapped: ${unmapped.length}`}
                  />
                </Tooltip>
              )}
            </>
          )}
        </Stack>

        {/*
         * Map wrapper.
         *
         * IMPORTANT: Google Maps mutates the DOM inside whatever element
         * we hand to `new google.maps.Map(el, ...)` — it appends tiles,
         * controls, copyright nodes, etc. If React is *also* rendering
         * children into that same element (e.g. a loading spinner), the
         * two fight over the DOM and React crashes on unmount with
         * "Failed to execute 'removeChild' on 'Node'".
         *
         * The standard fix: give Maps its OWN container (`mapDivRef`)
         * which React never populates after mount, and render overlays
         * as siblings of that container inside a relatively-positioned
         * wrapper.
         */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 380,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)'),
          }}
        >
          <div
            ref={containerRef}
            style={{ width: '100%', height: '100%' }}
          />

          {(loading || (!mapRef.current && !mapError)) && (
            <Box sx={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1,
              backgroundColor: 'rgba(255,255,255,0.35)',
              pointerEvents: 'none',
            }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!loading && points.length === 0 && !fetchError && mapRef.current && (
            <Box sx={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              zIndex: 1, pointerEvents: 'none',
            }}>
              <Typography variant="body2" color="text.secondary">
                No geographic data for the selected range.
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
